import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { planById } from '../config/plans';
import { useHasPlan } from '../hooks/usePlan';
import type { SubscriptionPlan } from '../types';
import { Button } from './ui/Button';
import { EmptyState } from './ui/EmptyState';

export interface PlanGateProps {
  /** Plans allowed to see `children`. */
  plans: SubscriptionPlan[];
  children: ReactNode;
  /** Custom fallback; defaults to an upgrade prompt. */
  fallback?: ReactNode;
  /** Name of the gated feature, used in the default prompt. */
  feature?: string;
}

export function UpgradePrompt({ feature, plans }: { feature?: string; plans: SubscriptionPlan[] }) {
  const lowest = planById(plans[0]);
  return (
    <EmptyState
      icon={<Lock />}
      title={`${feature ?? 'This feature'} is available on ${lowest.name} and above`}
      description="Upgrade your plan to unlock it. Changes take effect immediately."
      action={
        <Link to="/app/subscription">
          <Button>Upgrade to {lowest.name}</Button>
        </Link>
      }
    />
  );
}

export function PlanGate({ plans, children, fallback, feature }: PlanGateProps) {
  const allowed = useHasPlan(plans);
  if (allowed) return <>{children}</>;
  return <>{fallback ?? <UpgradePrompt feature={feature} plans={plans} />}</>;
}
